import { useRef, useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import SolutionsHero from "./components/SolutionsHero";
import SolutionsBlocks from "./components/SolutionsBlocks";
import SolutionsProcess from "./components/SolutionsProcess";
import SolutionsCta from "./components/SolutionsCta";
import { useSEO, buildBreadcrumbLd } from "@/utils/seo";

type TabId = "info-security" | "it-infrastructure" | "intelligent-building";

const VALID_TABS: TabId[] = ["info-security", "it-infrastructure", "intelligent-building"];

const TAB_SEO: Record<TabId, { title: string; description: string; keywords: string[]; name: string }> = {
  "info-security": {
    name: "信息安全",
    title: "信息安全综合防护解决方案",
    description:
      "欧格登信息安全综合防护方案，覆盖防火墙、入侵检测、零信任架构、SOC安全运营、等保合规、数据安全，助力企业抵御勒索病毒、APT攻击和内部威胁。",
    keywords: ["信息安全方案", "零信任", "SOC安全运营", "等保三级", "数据安全", "勒索病毒防护"],
  },
  "it-infrastructure": {
    name: "IT基础架构",
    title: "IT基础架构解决方案",
    description:
      "欧格登IT基础架构方案，覆盖数据中心、云计算、超融合、存储备份、网络建设与运维托管，助力企业构建高可用、高性能、可扩展的数字化底座。",
    keywords: ["IT基础架构", "数据中心", "超融合", "云计算", "存储备份", "网络建设"],
  },
  "intelligent-building": {
    name: "弱电智能化",
    title: "弱电智能化解决方案",
    description:
      "欧格登弱电智能化方案，覆盖视频监控、门禁一卡通、楼宇自控、综合布线、会议音视频与智慧园区，打造安全舒适高效的智能办公空间。",
    keywords: ["弱电智能化", "视频监控", "门禁系统", "楼宇自控", "综合布线", "智慧园区"],
  },
};

export default function SolutionsPage() {
  const [activeTab, setActiveTab] = useState<TabId>("info-security");
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();

  const seo = TAB_SEO[activeTab];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    path: `/solutions?category=${activeTab}`,
    jsonLd: buildBreadcrumbLd([
      { name: "首页", path: "/" },
      { name: "解决方案", path: "/solutions" },
      { name: seo.name, path: `/solutions?category=${activeTab}` },
    ]),
  });

  const blocksRef = useRef<HTMLDivElement>(null);

  const blockRefs = {
    "info-security": useRef<HTMLDivElement>(null),
    "it-infrastructure": useRef<HTMLDivElement>(null),
    "intelligent-building": useRef<HTMLDivElement>(null),
  };

  const scrollToBlocks = (delay = 300) => {
    setTimeout(() => {
      const el = blocksRef.current;
      if (el) {
        const offset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, delay);
  };

  // 支持 ?category=xxx 和 #xxx 两种方式切换 tab
  useEffect(() => {
    const category = searchParams.get("category") as TabId | null;
    const hashTab = hash ? hash.slice(1) as TabId : null;
    const target = category || hashTab;

    if (target && VALID_TABS.includes(target)) {
      setActiveTab(target);
      scrollToBlocks(300);
    }
  }, [searchParams, hash]);

  const handleScrollTo = (id: string) => {
    setActiveTab(id as TabId);
    scrollToBlocks(50);
  };

  return (
    <main className="min-h-screen">
      <SolutionsHero onScrollTo={handleScrollTo} />
      <div ref={blocksRef}>
        <SolutionsBlocks blockRefs={blockRefs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      <SolutionsProcess />
      <SolutionsCta />
    </main>
  );
}
